"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LincdServerProxy = void 0;
const Shape_1 = require("lincd/lib/shapes/Shape");
const JSONWriter_1 = require("lincd-jsonld/lib/utils/JSONWriter");
const JSONParser_1 = require("lincd-jsonld/lib/utils/JSONParser");
const lincd_1 = require("lincd/lib/ontologies/lincd");
const npm_1 = require("lincd/lib/ontologies/npm");
const CoreMap_1 = require("lincd/lib/collections/CoreMap");
const LinkedStorage_1 = require("lincd/lib/utils/LinkedStorage");
function isNodejs() {
    return (typeof 'process' !== 'undefined' &&
        process &&
        process.versions &&
        process.versions.node);
}
class LincdServerProxy extends Shape_1.Shape {
    /**
     * Default headers to be sent with every request
     *
     * @param headers
     */
    static addDefaultHeaders(headers) {
        this.defaultHeaders = Object.assign(this.defaultHeaders, headers);
    }
    static registerActionHandler(actionName, handler) {
        if (!this.actionHandlers.has(actionName)) {
            this.actionHandlers.set(actionName, []);
        }
        this.actionHandlers.get(actionName).push(handler);
    }
    /**
     * Create a response action object that can be returned by a server call to trigger an action on the frontend.
     * @param actionName
     * @param args
     */
    static createResponseAction(actionName, ...args) {
        return { [LincdServerProxy.RESPONSE_ACTION_KEY]: actionName, args };
    }
    call(shapeOrPackageName, method, ...args) {
        return __awaiter(this, void 0, void 0, function* () {
            if (typeof shapeOrPackageName === 'string') {
                return this.callBackendMethod(shapeOrPackageName, method, args);
            }
            else {
                return this.callShapeMethod(shapeOrPackageName, method, args);
            }
        });
    }
    callCustomShapeMethod(shape, method, methodName, body, headers) {
        let { shapeClass, SHACL_Shape, packageName, shapeURI } = this.parseShape(shape);
        //NOTE: custom calls are not going straight to the localServer on nodejs, so that request.body is available.
        return fetch(`${this.namedNode.uri}/call/${packageName}/${shapeClass.name}/${methodName}?shapeURI=${SHACL_Shape.namedNode.uri}`, {
            method: method,
            headers: headers || {}, //NOTE: custom shape methods do not use LincdServerProxy.defaultHeaders,
            body,
        })
            .then((res) => {
            if (res.ok) {
                return res.json();
            }
            else {
                console.warn('Could not complete server call: ' + res.statusText);
            }
        })
            .then((json) => {
            if (json.__action) {
                this.handleResponseAction(json.__action);
            }
            return JSONParser_1.JSONParser.parseObject(json).then(({ content }) => {
                return content;
            });
        })
            .catch((err) => {
            console.warn('Error during server call: ', err);
        });
    }
    customPost(route, ...args) {
        return __awaiter(this, void 0, void 0, function* () {
            //send data but not graphs (because frontend will have different graphs)
            let body = yield JSONWriter_1.JSONWriter.stringify(args, true, false);
            return this.fetchBackend(`${this.namedNode.uri}${route}`, body);
        });
    }
    callBackendMethod(packageName, methodOrConfig, args) {
        return __awaiter(this, void 0, void 0, function* () {
            let [method, headers, setLoaded] = this.parseMethod(methodOrConfig);
            //if this IS the backend
            if (this.localServer) {
                //then call the server directly
                return this.localServer.callBackendMethod(packageName, method, args);
            }
            else {
                //send data but not graphs (because frontend will have different graphs)
                let body = yield JSONWriter_1.JSONWriter.stringify({ args }, true, false);
                return this.fetchBackend(`${this.namedNode.uri}/call/${packageName}/${method}`, body, headers, setLoaded);
            }
        });
    }
    parseMethod(method) {
        if (typeof method === 'string') {
            return [method];
        }
        else {
            return [
                method.method,
                method.headers,
                method.setLoaded,
                method.overwriteData,
            ];
        }
    }
    callShapeMethod(shape, methodOrConfig, args) {
        return __awaiter(this, void 0, void 0, function* () {
            let [method, headers, setLoaded, overwriteData] = this.parseMethod(methodOrConfig);
            let { shapeClass, shapeURI, packageName } = this.parseShape(shape);
            if (this.localServer) {
                return this.localServer.callShapeMethod(packageName, method, shapeURI, shape instanceof Shape_1.Shape ? shape.node : null, args);
            }
            //SHACL Shapes are generated by using @linkedShape.
            //They are given a unique but deterministic temporary URI
            //We send that URI over to the server, which will have generated the same URI and thus recognise the shape
            //other params: send data but not graphs (because frontend will have different graphs)
            let body = yield JSONWriter_1.JSONWriter.stringify({
                shapeURI,
                instanceNode: shape instanceof Shape_1.Shape ? shape.node : null,
                args,
            }, true, false);
            return this.fetchBackend(`${this.namedNode.uri}/call/${packageName}/${shapeClass.name}/${method}`, body, headers, setLoaded, overwriteData);
        });
    }
    parseShape(shape) {
        let shapeClass;
        if (shape instanceof Shape_1.Shape) {
            shapeClass = Object.getPrototypeOf(shape).constructor;
        }
        else {
            shapeClass = shape;
        }
        let SHACL_Shape = shapeClass.shape;
        let shapeURI = SHACL_Shape.namedNode.uri;
        //get the shape class that defines this shape
        let shapeClassNode = SHACL_Shape.getOneInverse(lincd_1.lincd.definesShape);
        //get the module that exports this shape class
        let packageNode = shapeClassNode.getOne(lincd_1.lincd.module);
        let packageName = packageNode.getValue(npm_1.npm.packageName);
        return {
            shapeClass,
            SHACL_Shape,
            shapeURI,
            shapeClassNode,
            packageNode,
            packageName,
        };
    }
    fetchBackend(url, body, headers, setLoaded = false, overwriteData = false) {
        return __awaiter(this, void 0, void 0, function* () {
            // console.log(url, 'fetching', body);
            return fetch(url, {
                method: 'POST',
                headers: Object.assign(LincdServerProxy.defaultHeaders, headers || {}),
                body,
            })
                .then((res) => {
                if (res.ok) {
                    // console.log(url, 'response: ' + res.status + ' ' + res.statusText);
                    return res.json().catch((err) => {
                        console.warn('Could not parse JSON from response: ', err);
                        res
                            .text()
                            .then((text) => {
                            console.warn('Response text: ', text);
                        })
                            .catch((err) => {
                            console.warn('Also could not parse text from response. ', err);
                        });
                    });
                }
                else {
                    console.warn('Could not complete server call: ' + res.statusText);
                }
            })
                .then((json) => {
                //if instead of a normal data response the server returns an action, handle it
                if (json && json[LincdServerProxy.RESPONSE_ACTION_KEY]) {
                    if (this.handleResponseAction(json[LincdServerProxy.RESPONSE_ACTION_KEY])) {
                        return;
                    }
                }
                // console.log(url, 'converting response to native objects: ' + json);
                //added defaultStorageGraph, because before that data coming in from backend calls was added to the default graph
                //but then changing any properties of the nodes in this data would cause graph changes in LincdStorage.assignQuadsToGraph -> moveAllQuadsOfNodeIfRequired
                //NOTE: only as long as there is one main storage graph. If there are multiple, this will need to be updated
                //Ideally all quads parsed here already have a graph, so that this is not necessary
                return JSONParser_1.JSONParser.parseObject(json, LinkedStorage_1.LinkedStorage.getDefaultStorageGraph(), setLoaded, null, overwriteData).then(({ content }) => {
                    // console.log(url, 'response: ' + content.toString());
                    return content;
                });
            })
                .catch((err) => {
                console.warn('Error during server call: ', err);
            });
        });
    }
    handleResponseAction(action) {
        let preventdefault = false;
        if (LincdServerProxy.actionHandlers.has(action)) {
            LincdServerProxy.actionHandlers.get(action).forEach((handler) => {
                handler({ preventDefault: () => (preventdefault = true) });
            });
        }
        return preventdefault;
    }
}
exports.LincdServerProxy = LincdServerProxy;
/**
 * Use this key in a json response to trigger an action on the frontend.
 */
LincdServerProxy.RESPONSE_ACTION_KEY = '__action';
LincdServerProxy.actionHandlers = new CoreMap_1.CoreMap();
LincdServerProxy.defaultHeaders = {
    Accept: 'application/json, text/plain, */*',
    'Content-Type': 'application/json',
};
//# sourceMappingURL=LincdServerProxy.js.map